// src/pages/usa/colorado/Breckenridge.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Breckenridge ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";   // ← use THIS if your map is here
// export const backRoute = "/travel/usa/colorado"; // ← use THIS if your map is here

export default function Breckenridge() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__breckenridgeBackRouteSet) {
      window.__breckenridgeBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>

      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Breckenridge, Colorado</h1>

        <section className="travel-review">
          <div className="travel-text">
            <h2>🏞️ Discover Breckenridge, Colorado</h2>


            <p>
              Breckenridge is a charming mountain town nestled in the heart of Colorado's Rocky Mountains.
              Known for its vibrant arts scene, historic architecture, and world-class ski resorts,
              Breckenridge offers a perfect blend of outdoor adventure and cultural experiences.
              Whether you're hitting the slopes in winter or exploring scenic trails in summer,
              Breckenridge is a must-visit destination for nature lovers and culture enthusiasts alike.
            </p>

            <p>
              In winter months, Breckenridge transforms into a snowy wonderland, attracting skiers and
              snowboarders from around the world. The town's historic Main Street is lined with cozy shops,
              art galleries, and restaurants, creating a lively atmosphere year-round. With its stunning
              mountain views and welcoming community, Breckenridge is a true gem in Colorado's outdoor paradise.
            </p>

            <p>
              During summer, Breckenridge offers a wide range of outdoor activities, including hiking,
              mountain biking, and fishing. The nearby Breckenridge Ski Resort provides scenic gondola rides
              with incredible views of the surrounding peaks. The town also hosts festivals and events
              throughout the year, celebrating its rich history and vibrant culture. Whether you're seeking
              outdoor adventure or a relaxing mountain getaway, Breckenridge has something for everyone.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              The drive from Denver to Breckenridge takes approximately 1.5 to 2 hours, depending on traffic
              and weather conditions. The route takes you through scenic mountain landscapes, making it a
              beautiful trip. It's advisable to check road conditions during winter months, as snow can impact
              travel times. Once in Breckenridge, parking is available in various lots and garages throughout
              the town, with some accommodations offering shuttle services to nearby attractions.
            </p>

            {/* 🌟 PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>The Town:</strong> Breckenridge’s Main Street has a historic, colorful look
                  that makes the whole town feel warm and welcoming. It’s full of shops, restaurants,
                  cafés, and art galleries, so it’s easy to spend hours walking around after a day on
                  the slopes or trails. The free gondola ride is one of my favorite things — the views
                  of the mountains are incredible, and it’s something everyone should do at least once.
                  Breckenridge also hosts great year‑round events like the International Snow Sculpture
                  Championships, Breckenridge Film Festival, Breckenridge Music Festival, Oktoberfest,
                  Ullr Fest, the Santa Parade, and more. The town always feels alive no matter the season.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Peak 8 Base Area:</strong> Peak 8 is the main hub of Breckenridge Ski Resort
                  and has a fun, energetic atmosphere. You’ll find ski‑in/ski‑out access, restaurants,
                  bars, rental shops, and plenty of places to relax after a day on the mountain. During
                  ski season, there’s usually music, events, and a lively après‑ski scene. In the summer,
                  the area turns into an adventure base with mountain biking, hiking trails, the alpine
                  coaster, and scenic gondola rides with amazing views of the surrounding peaks.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Isak Heartstone:</strong> This giant wooden troll is one of the most unique
                  art pieces in Breckenridge. Created by artist Thomas Dambo, Isak Heartstone is built
                  from recycled materials and blends perfectly with the forest around him. The short walk
                  to reach the sculpture is peaceful, and the area has spots to sit and enjoy the scenery.
                  It’s a fun and memorable stop, especially if you enjoy art mixed with nature.
                </div>
              </li>
            </ul>

            {/* 🥾 HIDDEN GEMS */}
            <h3>🥾 Hidden Gems in Breckenridge</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Country Boy Mine Tour:</strong>
                  One of the best historic mine tours in the area. You can walk inside the old
                  tunnels, learn about Breckenridge’s mining past, and even try gold panning.
                  It’s fun, educational, and great for families.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=39.481179336763, -106.01429552646378"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Sallie Barber Mine:</strong>
                  A short, easy hike leads to this historic mine site with great views and
                  old mining structures. It’s a peaceful spot and a nice break from the busier
                  areas around town.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=39.48691279473027, -105.98056082105336"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Boreas Pass Road:</strong>
                  A scenic drive with mountain views, old railroad history, and plenty of
                  photo spots. If you continue all the way to the end, the road drops you
                  into the small historic town of Como — a cool surprise most visitors don’t
                  expect.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=39.46276182862069, -106.02065467081219"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Quandary Peak (14er):</strong>
                  One of Colorado’s most popular 14ers and the closest one to Breckenridge.
                  The hike is challenging but doable for many hikers, with rewarding summit
                  views over the Tenmile Range.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=39.38539846080397, -106.06195353544403"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>High Line Railroad Park:</strong>
                  A small but interesting stop with historic railroad equipment, including
                  Engine No. 9 from the old narrow‑gauge line that once crossed Boreas Pass.
                  Great for quick photos and local history.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=39.47393919991811, -106.04098121582258"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* 🛏️ PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Breckenridge</h3>

            <p>
              Breckenridge offers a wide range of lodging options, from ski‑in/ski‑out resorts to cozy lodges.
              These well‑rated choices appear on Booking.com and Expedia unless noted otherwise:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Beaver Run Resort:</strong> Ski‑in/ski‑out access, multiple pools, and great mountain views.
                  <em> (Listed on Booking & Expedia)</em>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Grand Colorado on Peak 8:</strong> Modern, upscale, and right on the slopes.
                  <em> (Listed on Booking & Expedia)</em>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Village at Breckenridge:</strong> Perfect location near Main Street and the lifts.
                  <em> (Listed on Booking & Expedia)</em>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Mountain Thunder Lodge:</strong> Quiet, cabin‑style lodging with gondola access.
                  <em> (Listed on Booking & Expedia)</em>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>DoubleTree by Hilton Breckenridge:</strong> Reliable, comfortable, and close to Peak 9.
                  <em> (Listed on Booking & Expedia)</em>
                </div>
              </li>
            </ul>

            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                        <div className="affiliate-block">
                          <AffiliateExpedia />
                          <AffiliateBooking />
                          <AffiliateTrip />
                        </div>

            {/* 🎥 VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos from Breckenridge.</p>

            <p>
              <a
                href="https://youtu.be/HwbjkBmn7wo?si=ZzZ4k62LodjXLRhQ"
                target="_blank"
                rel="noopener noreferrer"
              >
                Breckenridge Peak 8 and Gondola Ride
              </a>
              <br />
              <a
                href="https://youtu.be/E9N7MAmq700?si=QbfjMu4PGMM5DEW9"
                target="_blank"
                rel="noopener noreferrer"
              >
                Breckenridge Main Street and Town Walkthrough
              </a>
            
             <br />
              <a
                href="https://youtu.be/FjmPGnWj45g?si=9aQ1ZrC4R4gm8-jE"
                target="_blank"
                rel="noopener noreferrer"
              >
                Isak Heartstone Sculpture Walkthrough
              </a>
               <br />
              <a
                href="https://youtu.be/hQ01YGuD4ls?si=ftSxFsxUtDLoJXtX"
                target="_blank"
                rel="noopener noreferrer"
              >
                Isak Heartstone Sculpture
              </a>
                <br />
              <a
                href="https://youtu.be/6vh8gsWnYtU?si=1wMd7PVLFymzRafj"
                target="_blank"
                rel="noopener noreferrer"
              >
                Alpine Coaster Ride at Breckenridge
              </a>
               <br />
              <a
                href="https://youtu.be/55L0NAeU6js?si=snp-JyIG5NF8ZTGC"
                target="_blank"
                rel="noopener noreferrer"
              >
                Gold Runner Mountain Coaster Ride at Breckenridge
              </a>
              <br/>
              <a
                href="https://youtu.be/2Fmv7W6ND8A?si=825bDB3nP5ae1VQr"
                target="_blank"
                rel="noopener noreferrer"
              >
                Boreas Pass Road Scenic Drive and Views
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES (kept the same for now) */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/hQ01YGuD4ls?si=ftSxFsxUtDLoJXtX"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/breckenridge/trol.jpg"
                alt="Isak Heartstone Sculpture Video"
              />
            </a>

            <a
              href="https://youtu.be/E9N7MAmq700?si=bIsiPxWhjsr4duva"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/breckenridge/casa4.jpg"
                alt="Breckenridge Town Video"
              />
            </a>

            <a
              href="https://youtu.be/HwbjkBmn7wo?si=CCMi1vGL-nhR_L3q"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/breckenridge/gondola.jpg"
                alt="Breckendridge Peak 8 Gondola Video"
              />
            </a>

            {/* Aspen images kept for now — you will replace manually */}
            <img src="/images/usa/colorado/breckenridge/tren.jpg" alt="train" />
            <img src="/images/usa/colorado/breckenridge/vias.jpg" alt="Railroad" />
            <img src="/images/usa/colorado/breckenridge/trensign.jpg" alt="train sign" />
            <img src="/images/usa/colorado/breckenridge/casa1.jpg" alt="House" />
            <img src="/images/usa/colorado/breckenridge/casa2.jpg" alt="House" />
            <img src="/images/usa/colorado/breckenridge/casa3.jpg" alt="House" />
            <img src="/images/usa/colorado/breckenridge/casa5.jpg" alt="House" />
            <img src="/images/usa/colorado/breckenridge/barco.jpg" alt="Boat" />
            <img src="/images/usa/colorado/breckenridge/lago2.jpg" alt="Lake" />
            <img src="/images/usa/colorado/breckenridge/lago3.jpg" alt="Lake" />
            <img src="/images/usa/colorado/breckenridge/Lake.jpg" alt="Lake" />
            <img src="/images/usa/colorado/breckenridge/robot.jpg" alt="Robot" />
            <img src="/images/usa/colorado/breckenridge/entrada.jpg" alt="Entrance" />
            
          </div>
        </section>
      </div>

      {/* Centered email button */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
